import '@/plugins/lodash';
import '@/plugins/moment';
import '@/plugins/ladda';
import 'ladda/dist/ladda.min.css';
import '@/plugins/axios';
import registerGlobalComponents from '@/components';
import SvgVue from '@/components/base/svg-vue';
import {applyDialogBackdropSettings} from '@/utils/dialog-backdrop';
import {getNavigationShortcutBadgesForTarget} from '@/utils/navigation-shortcuts';
import {refreshPwaBadgeCount} from '@/utils/pwa-badge';
import {createAccessHelpers} from '@/utils/access-context';
import {getAuthToken} from '@/utils/auth-token';
import ClickawayCompat from '@/plugins/vue-clickaway-compat';

import {createApp, h} from 'vue';
import Notifications from '@kyvg/vue3-notification';
import {VueReCaptcha} from "vue-recaptcha-v3";
import VueElementLoading from 'vue-element-loading';
import VueSimpleContextMenu from 'vue-simple-context-menu';

import store from '@/store';
import App from "@/views/app";
import i18n from "@/language";
import router from "@/views/router";

const escapeHtml = function (value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
};

const highlightMarkup = function (raw) {
    return escapeHtml(raw)
        .replace(/(&lt;!--[\s\S]*?--&gt;)/g, '<span class="token comment">$1</span>')
        .replace(/(&lt;!DOCTYPE[\s\S]*?&gt;)/gi, '<span class="token keyword">$1</span>')
        .replace(/(&lt;\/?)([a-zA-Z0-9:_-]+)([\s\S]*?)(\/?&gt;)/g, function (_all, open, tag, attrs, close) {
            const highlightedAttrs = attrs.replace(/([a-zA-Z_:-]+)(\s*=\s*)(".*?"|'.*?')/g, '<span class="token attr-name">$1</span>$2<span class="token attr-value">$3</span>');
            return '<span class="token punctuation">' + open + '</span><span class="token tag">' + tag + '</span>' + highlightedAttrs + '<span class="token punctuation">' + close + '</span>';
        });
};

const highlightJsonLike = function (raw) {
    return escapeHtml(raw)
        .replace(/("(?:\\.|[^"\\])*")(\s*:)/g, '<span class="token attr-name">$1</span>$2')
        .replace(/:\s*("(?:\\.|[^"\\])*")/g, ': <span class="token string">$1</span>')
        .replace(/\b(true|false|null)\b/g, '<span class="token boolean">$1</span>')
        .replace(/\b-?(?:0|[1-9]\d*)(?:\.\d+)?(?:[eE][+\-]?\d+)?\b/g, '<span class="token number">$&</span>');
};

const highlightCode = function (raw, language) {
    const normalizedLanguage = String(language || '').toLowerCase();
    if (normalizedLanguage === 'markup' || normalizedLanguage === 'xml' || normalizedLanguage === 'html') {
        return highlightMarkup(raw);
    }
    if (normalizedLanguage === 'javascript' || normalizedLanguage === 'json') {
        return highlightJsonLike(raw);
    }

    let highlighted = escapeHtml(raw);
    if (normalizedLanguage === 'css') {
        highlighted = highlighted
            .replace(/([.#]?[a-zA-Z0-9_-]+)(\s*\{)/g, '<span class="token selector">$1</span>$2')
            .replace(/([a-zA-Z-]+)(\s*:)/g, '<span class="token attr-name">$1</span>$2')
            .replace(/:\s*([^;}{]+)/g, ': <span class="token attr-value">$1</span>');
    }
    if (normalizedLanguage === 'php' || normalizedLanguage === 'python') {
        highlighted = highlighted.replace(/\b(class|function|return|if|else|for|while|try|catch|public|private|protected|const|let|var|def|import)\b/g, '<span class="token keyword">$1</span>');
    }
    return highlighted;
};

const formatMomentDateTimeAgo = function (value) {
    if (!value) {
        return '';
    }
    return moment(value).locale(window.app.app_date_locale).fromNow();
};

const formatMomentDateTime = function (value) {
    if (!value) {
        return '';
    }
    return moment(value).locale(window.app.app_date_locale).format(window.app.app_date_format + ' H:mm');
};

const formatPrettyBytes = function (value) {
    const size = Number(value);
    if (!Number.isFinite(size) || size < 1) {
        return '0 B';
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
    const normalized = size / Math.pow(1024, exponent);
    const precision = normalized >= 100 || exponent === 0 ? 0 : 1;

    return normalized.toFixed(precision) + ' ' + units[exponent];
};

const normalizeLocale = function (value) {
    return String(value || 'en').trim().toLowerCase() || 'en';
};

const enhanceCodeBlocks = function (rootNode) {
    const root = rootNode || document;
    if (!root || !root.querySelectorAll) {
        return;
    }

    const blockCandidates = [];
    const selectors = 'pre[data-line-numbers], pre[data-syntax-highlight], pre[data-code-language], pre.line-numbers, pre[class*="language-"], pre.code-sample-plain, pre > code[class*="language-"], pre > code[data-code-language], pre > code[data-line-numbers], pre > code[data-syntax-highlight]';
    if (root.matches && root.matches(selectors)) {
        blockCandidates.push(root);
    }
    blockCandidates.push(...root.querySelectorAll(selectors));

    const blocks = [];
    const seen = new Set();
    blockCandidates.forEach(function (node) {
        const pre = node && node.tagName === 'PRE' ? node : (node && node.closest ? node.closest('pre') : null);
        if (!pre || seen.has(pre)) {
            return;
        }
        seen.add(pre);
        blocks.push(pre);
    });

    blocks.forEach(function (pre) {
        const codeNode = pre.querySelector('code') || pre;
        const languageClass = String(pre.className || '').match(/(?:^|\s)language-([a-z0-9_-]+)/i);
        const codeLanguageClass = codeNode && codeNode !== pre
            ? String(codeNode.className || '').match(/(?:^|\s)language-([a-z0-9_-]+)/i)
            : null;
        const language = pre.getAttribute('data-code-language')
            || (codeNode !== pre ? codeNode.getAttribute('data-code-language') : '')
            || (languageClass ? languageClass[1] : '')
            || (codeLanguageClass ? codeLanguageClass[1] : '');
        const syntaxEnabled = pre.hasAttribute('data-syntax-highlight')
            ? pre.getAttribute('data-syntax-highlight') !== '0'
            : (codeNode !== pre && codeNode.hasAttribute('data-syntax-highlight')
                ? codeNode.getAttribute('data-syntax-highlight') !== '0'
                : !!language);
        const lineNumbersEnabled = pre.hasAttribute('data-line-numbers')
            ? pre.getAttribute('data-line-numbers') === '1'
            : (codeNode !== pre && codeNode.hasAttribute('data-line-numbers')
                ? codeNode.getAttribute('data-line-numbers') === '1'
                : pre.classList.contains('line-numbers'));

        if (syntaxEnabled && language && !String(pre.className || '').match(/(?:^|\s)language-/i)) {
            pre.classList.add('language-' + language);
        }
        if (!syntaxEnabled) {
            pre.className = String(pre.className || '')
                .split(/\s+/)
                .filter(function (token) {
                    return token && !/^language-/i.test(token);
                })
                .join(' ');
            pre.classList.add('code-sample-plain');
        } else {
            pre.classList.remove('code-sample-plain');
        }

        const rawCode = codeNode.textContent || '';
        if (syntaxEnabled) {
            codeNode.innerHTML = highlightCode(rawCode, language || 'markup');
            if (!String(codeNode.className || '').match(/(?:^|\s)language-/i)) {
                codeNode.classList.add('language-' + (language || 'markup'));
            }
        } else {
            codeNode.textContent = rawCode;
            codeNode.className = String(codeNode.className || '')
                .split(/\s+/)
                .filter(function (token) {
                    return token && !/^language-/i.test(token);
                })
                .join(' ');
        }

        const existingRows = pre.querySelector('.line-numbers-rows');
        if (existingRows) {
            existingRows.remove();
        }

        if (!lineNumbersEnabled) {
            pre.classList.remove('line-numbers');
            return;
        }

        pre.classList.add('line-numbers');
        const text = (pre.querySelector('code') || pre).textContent || '';
        const normalizedText = text.replace(/\r\n?/g, '\n');
        const endsWithNewline = /\n$/.test(normalizedText);
        const lineCount = Math.max(normalizedText.split('\n').length - (endsWithNewline ? 1 : 0), 1);
        const rows = document.createElement('span');
        rows.className = 'line-numbers-rows';
        for (let i = 0; i < lineCount; i += 1) {
            rows.appendChild(document.createElement('span'));
        }
        pre.appendChild(rows);
    });
};

window.__enhanceCodeBlocks = enhanceCodeBlocks;

const registerPwaServiceWorker = function () {
    if (!window.app || !window.app.pwa_enabled) {
        return;
    }
    if (!('serviceWorker' in navigator)) {
        return;
    }

    window.addEventListener('load', function () {
        navigator.serviceWorker.register('/service-worker.js').then(function (registration) {
            if (!window.app.pwa_force_update) {
                return;
            }

            if (registration.waiting) {
                registration.waiting.postMessage({type: 'SKIP_WAITING'});
            }

            registration.addEventListener('updatefound', function () {
                const installing = registration.installing;
                if (!installing) {
                    return;
                }
                installing.addEventListener('statechange', function () {
                    if (installing.state === 'installed' && registration.waiting) {
                        registration.waiting.postMessage({type: 'SKIP_WAITING'});
                    }
                });
            });

            let refreshing = false;
            navigator.serviceWorker.addEventListener('controllerchange', function () {
                if (refreshing) {
                    return;
                }
                refreshing = true;
                window.location.reload();
            });
        }).catch(function () {
        });
    });
};

registerPwaServiceWorker();

const app = createApp({
    render: () => h(App),
    mounted() {
        this.$store.commit('setSettings', {...(window.app || {})});
        applyDialogBackdropSettings(this.$store.state.settings || {});
        refreshPwaBadgeCount().catch(function () {
        });
        this.$nextTick(function () {
            enhanceCodeBlocks(document);
        });
        if (typeof MutationObserver !== 'undefined') {
            const observerTarget = document.getElementById('app') || document.body;
            if (!observerTarget) {
                return;
            }
            const observer = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    mutation.addedNodes.forEach(function (node) {
                        if (node && node.nodeType === 1) {
                            enhanceCodeBlocks(node);
                        }
                    });
                });
            });
            observer.observe(observerTarget, {childList: true, subtree: true});
        }
    }
});

app.use(store);
app.use(router);
app.use(i18n);
app.use(Notifications);
app.use(ClickawayCompat);
if (window.app.recaptcha_enabled) {
    app.use(VueReCaptcha, {siteKey: window.app.recaptcha_public});
}

registerGlobalComponents(app);
app.component('SvgVue', SvgVue);
app.component('svg-vue', SvgVue);
app.component('VueElementLoading', VueElementLoading);
app.component('vue-simple-context-menu', VueSimpleContextMenu);

app.config.globalProperties.$momentFormatDateTimeAgo = formatMomentDateTimeAgo;
app.config.globalProperties.$momentFormatDateTime = formatMomentDateTime;
app.config.globalProperties.$prettyBytes = formatPrettyBytes;
app.config.globalProperties.$access = createAccessHelpers(store);
app.config.globalProperties.$navigationShortcutBadges = function (targetId = null, routePath = null) {
    return getNavigationShortcutBadgesForTarget({
        store,
        targetId,
        routePath,
    });
};

router.afterEach(function () {
    refreshPwaBadgeCount().catch(function () {
    });
});

const bootstrapLocale = function () {
    const locale = normalizeLocale(document.documentElement.lang);
    document.documentElement.lang = locale;
    i18n.global.locale = locale;

    return axios.get('api/lang/' + locale)
        .then(function (response) {
            i18n.global.mergeLocaleMessage(locale, response.data);
        })
        .catch(function () {
        });
};

const bootstrapAuthenticatedUser = function () {
    if (!getAuthToken()) {
        return Promise.resolve();
    }

    return axios.get('api/auth/user')
        .then(function (response) {
            store.commit('updateUser', response.data);
        })
        .catch(function () {
            // The route guard handles invalid or expired authentication.
        });
};

Promise.all([
    bootstrapLocale(),
    bootstrapAuthenticatedUser(),
]).finally(function () {
    app.mount('#app');
});
